
import React, { useEffect, useRef } from 'react';

const AnimatedOilDrop: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  
  useEffect(() => {
    if (!canvasRef.current) return;
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    // Set canvas dimensions
    canvas.width = 300;
    canvas.height = 300;
    
    const droplets: {
      x: number;
      y: number;
      radius: number;
      speed: number;
      color: string;
      opacity: number;
      direction: number;
      pulseSpeed: number;
      pulseDirection: number;
      maxRadius: number;
      minRadius: number;
    }[] = [];
    
    // Create initial droplets
    for (let i = 0; i < 20; i++) {
      createDroplet();
    }
    
    function createDroplet() {
      const colors = ['#FFB400', '#002F6C', '#1A4585', '#001F4A'];
      const radius = Math.random() * 8 + 2;
      droplets.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        radius: radius,
        speed: Math.random() * 1 + 0.5,
        color: colors[Math.floor(Math.random() * colors.length)],
        opacity: Math.random() * 0.7 + 0.3,
        direction: Math.random() > 0.5 ? 1 : -1,
        pulseSpeed: Math.random() * 0.02 + 0.01,
        pulseDirection: 1,
        maxRadius: radius * 1.5,
        minRadius: radius * 0.7
      });
    }

    // Add interactive effects
    let mouseX = 0;
    let mouseY = 0;
    let isMouseOver = false;

    canvas.addEventListener('mousemove', (e) => {
      const rect = canvas.getBoundingClientRect();
      mouseX = e.clientX - rect.left;
      mouseY = e.clientY - rect.top;
      isMouseOver = true;
    });

    canvas.addEventListener('mouseout', () => {
      isMouseOver = false;
    });
    
    function animate() {
      // Clear canvas
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      // Update and draw droplets
      for (let i = 0; i < droplets.length; i++) {
        const droplet = droplets[i];
        
        // Move droplet
        droplet.y += droplet.speed;
        
        // Add slight horizontal movement
        droplet.x += Math.sin(Date.now() * 0.001 + i) * 0.3 * droplet.direction;
        
        // Pulse effect (grow and shrink)
        if (droplet.radius >= droplet.maxRadius) {
          droplet.pulseDirection = -1;
        } else if (droplet.radius <= droplet.minRadius) {
          droplet.pulseDirection = 1;
        }
        
        droplet.radius += droplet.pulseDirection * droplet.pulseSpeed;
        
        // Reset position if it goes out of bounds
        if (droplet.y > canvas.height + droplet.radius) {
          droplet.y = -droplet.radius;
          droplet.x = Math.random() * canvas.width;
        }
        
        // Interactive effect: repel droplets when mouse is near
        if (isMouseOver) {
          const dx = mouseX - droplet.x;
          const dy = mouseY - droplet.y;
          const distance = Math.sqrt(dx * dx + dy * dy);
          
          if (distance < 50) {
            // Repel droplet away from mouse
            const angle = Math.atan2(dy, dx);
            droplet.x -= Math.cos(angle) * (1 - distance / 50) * 2;
            droplet.y -= Math.sin(angle) * (1 - distance / 50) * 2;
            
            // Enhance glow effect when interacted with
            ctx.shadowBlur = 15;
            ctx.shadowColor = droplet.color;
          } else {
            ctx.shadowBlur = 0;
          }
        }
        
        // Draw droplet
        ctx.beginPath();
        ctx.arc(droplet.x, droplet.y, droplet.radius, 0, Math.PI * 2);
        ctx.fillStyle = droplet.color + Math.floor(droplet.opacity * 255).toString(16).padStart(2, '0');
        ctx.fill();
        
        // Draw shimmering effect
        ctx.beginPath();
        ctx.arc(droplet.x - droplet.radius * 0.3, droplet.y - droplet.radius * 0.3, droplet.radius * 0.2, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255, 255, 255, ${droplet.opacity * 0.8})`;
        ctx.fill();
        
        // Draw additional highlight for 3D effect
        ctx.beginPath();
        ctx.arc(droplet.x + droplet.radius * 0.5, droplet.y + droplet.radius * 0.5, droplet.radius * 0.1, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(0, 0, 0, ${droplet.opacity * 0.2})`;
        ctx.fill();
      }
      
      // Add new droplets occasionally
      if (Math.random() < 0.03 && droplets.length < 50) {
        createDroplet();
      }
      
      requestAnimationFrame(animate);
    }
    
    // Start animation
    animate();
    
    return () => {
      // Clean up event listeners
      canvas.removeEventListener('mousemove', () => {});
      canvas.removeEventListener('mouseout', () => {});
    };
  }, []);
  
  return (
    <div className="relative w-full h-full">
      <canvas 
        ref={canvasRef} 
        className="absolute top-0 left-0 w-full h-full object-cover"
      />
    </div>
  );
};

export default AnimatedOilDrop;
